// How much schedule detail a job owes, and whether the uploaded schedule has it.
//
// A $40M job planned as six bars is unusable — the daily field report has
// nothing to pace against — but the same six bars are exactly right on a $200k
// service call. So the required granularity scales with the job:
//
//   task / work package  the finest: each cost code broken into the activities
//                        that actually get worked (four tie-in crews, not 'Tie-ins')
//   cost code            one schedule activity per JDE phase
//   discipline           one per discipline (the Productivity Tracker's own grouping)
//   trade class          one per work type — pipeline, facilities, E&I
//
// Contract revenue picks the level. JDE's own Job_Tier is the cross-check: it
// only has three values for four levels and its ranges overlap badly (tier 3
// runs from $2k to $50M), so it can't drive the choice — but it can raise it,
// which matters on a big job whose Day Zero budget hasn't been keyed yet.
//
// Everything here is pure so the thresholds and the coverage math can be tested
// against plain rows; the UI lives in scheduleDetailLadder.js.

import { nameScore } from './quantityMatch';

// Coarse → fine. Index is the rank, so comparing two levels is comparing two
// indexes; nothing else in this file should hardcode an ordering.
export const LEVELS = ['tradeClass', 'discipline', 'costCode', 'task'];

export const LEVEL_LABEL = {
    tradeClass: 'Trade class',
    discipline: 'Discipline',
    costCode: 'Cost code',
    task: 'Task / work package',
};

export const LEVEL_HINT = {
    tradeClass: 'One activity per work type — pipeline, facilities, E&I.',
    discipline: 'One activity per discipline, as grouped on the Productivity Tracker.',
    costCode: 'One activity per JDE phase code.',
    task: 'Each cost code broken into the work packages the crews actually run.',
};

// Calibrated against the 901 active jobs carrying a Day Zero revenue: 25 land
// at task level, 71 cost code, 177 discipline, 628 trade class. Retune here —
// this constant is the only place the breakpoints exist.
export const REVENUE_TIERS = [
    { min: 10000000, level: 'task' },
    { min: 2000000, level: 'costCode' },
    { min: 500000, level: 'discipline' },
    { min: 0, level: 'tradeClass' },
];

// JDE Job_Tier (Form_Job_Master.Job_Tier, on jobData as Cat_Code_BU_16) never
// lowers the requirement, only raises it to this floor.
export const JOB_TIER_FLOOR = {
    3: 'costCode',
    2: 'discipline',
    1: 'tradeClass',
};

// Share of a level's units that need a schedule activity before the schedule
// counts as planned at that level. Not 100%: a real schedule always has a few
// codes nobody drew a bar for, and failing the job over two of eighty makes the
// flag noise.
const COVERAGE = 0.7;

// Two activities on one cost code is what separates 'broken into work packages'
// from 'one bar per phase'.
const TASK_MIN_ACTIVITIES = 2;

const rankOf = (level) => LEVELS.indexOf(level);

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const usd = (v) => {
    const n = num(v);
    if (n >= 1000000) return `$${(n / 1000000).toFixed(n >= 10000000 ? 0 : 1)}M`;
    if (n >= 1000) return `$${Math.round(n / 1000)}k`;
    return `$${Math.round(n).toLocaleString()}`;
};

/**
 * The finer of two levels. Either may be null/unknown.
 */
const finer = (a, b) => {
    const ra = rankOf(a);
    const rb = rankOf(b);
    if (ra < 0) return rb < 0 ? null : b;
    if (rb < 0) return a;
    return ra >= rb ? a : b;
};

/**
 * The detail level this job owes.
 *
 * @param revenue  contract revenue — sideMenuBudgets contractValuesRevised +
 *                 changeOrderRevenue.
 * @param jobTier  JDE Job_Tier ('1' | '2' | '3'), jobData.Cat_Code_BU_16.
 * @returns { level, label, fromRevenue, fromTier, raisedByTier, reason }
 */
export const requiredLevel = ({ revenue, jobTier } = {}) => {
    const rev = num(revenue);
    const fromRevenue = (REVENUE_TIERS.find((t) => rev >= t.min) || REVENUE_TIERS[REVENUE_TIERS.length - 1]).level;

    const tier = String(jobTier ?? '').trim();
    const fromTier = JOB_TIER_FLOOR[tier] || null;

    const level = finer(fromRevenue, fromTier);
    const raisedByTier = fromTier != null && rankOf(fromTier) > rankOf(fromRevenue);

    const parts = [];
    // A job with no Day Zero budget keyed reads as $0, which is a missing
    // number rather than a small job — say so instead of implying it's tiny.
    parts.push(rev > 0 ? `${usd(rev)} contract revenue` : 'no contract revenue entered');
    if (tier) parts.push(`Job Tier ${tier}`);

    return {
        level,
        label: LEVEL_LABEL[level],
        fromRevenue,
        fromTier,
        raisedByTier,
        revenue: rev,
        jobTier: tier || null,
        reason: parts.join(' · '),
    };
};

const codeOf = (r) => String(r?.Cost_Code ?? r?.costCode ?? '').trim();
const descOf = (r) => String(r?.Description ?? r?.description ?? '').trim();

const activityName = (a) => String(a?.Activity_Name ?? a?.activityName ?? '').trim();
const activityCode = (a) => String(a?.Activity_Code ?? a?.activityCode ?? '').trim();

/** Stable identity for an activity — the same key quantityMatch binds on. */
const keyOf = (a) => `${activityCode(a)}|${activityName(a)}`;

/**
 * How many distinct schedule activities each budget cost code has bound to it.
 *
 * `bindings` is what the mapping panel holds: rows carrying `costCode` and
 * either a `boundKeys` array or the saved comma-separated `activityCode`
 * string. Falling back to the string means a saved-but-not-yet-remapped
 * schedule still measures.
 */
export const bindingCounts = (bindings = []) => {
    const out = {};
    bindings.forEach((b) => {
        const code = codeOf(b);
        if (!code) return;
        const keys = Array.isArray(b.boundKeys) && b.boundKeys.length
            ? b.boundKeys
            : String(b.activityCode ?? b.Activity_Code ?? '').split(',').map((s) => s.trim()).filter(Boolean);
        if (!out[code]) out[code] = new Set();
        keys.forEach((k) => out[code].add(k));
    });
    return Object.fromEntries(Object.entries(out).map(([k, v]) => [k, v.size]));
};

/**
 * The finest level the uploaded schedule actually supports.
 *
 * @param bindings      mapping rows (see bindingCounts)
 * @param budgetRows    the budget's cost-code rows — what the schedule is
 *                      measured against, not what it happens to contain
 * @param disciplineMap cost code → discipline, off the Productivity Tracker
 * @param activities    the raw schedule activities, so an unmapped schedule
 *                      still registers as trade-class coverage
 * @returns { level, label, coverage: {task, costCode, discipline},
 *            unboundCodes, unboundDisciplines, thinCodes }
 */
export const achievedLevel = ({
    bindings = [], budgetRows = [], disciplineMap = {}, activities = [],
} = {}) => {
    const counts = bindingCounts(bindings);
    const codes = [...new Set(budgetRows.map(codeOf).filter(Boolean))];

    const unboundCodes = codes.filter((c) => !counts[c]);
    // Bound, but as a single bar — planned at cost-code level, not task level.
    const thinCodes = codes.filter((c) => counts[c] > 0 && counts[c] < TASK_MIN_ACTIVITIES);

    const boundCount = codes.filter((c) => counts[c] > 0).length;
    const taskCount = codes.filter((c) => counts[c] >= TASK_MIN_ACTIVITIES).length;

    // Disciplines are only as good as the map — a job that never filled in the
    // Productivity Tracker's discipline column has none, and the level simply
    // isn't measurable rather than failing.
    const disciplines = [...new Set(codes.map((c) => disciplineMap[c]).filter(Boolean))];
    const boundDisciplines = new Set(
        codes.filter((c) => counts[c] > 0).map((c) => disciplineMap[c]).filter(Boolean),
    );
    const unboundDisciplines = disciplines.filter((d) => !boundDisciplines.has(d));

    const share = (n, total) => (total > 0 ? n / total : 0);
    const coverage = {
        task: share(taskCount, codes.length),
        costCode: share(boundCount, codes.length),
        discipline: share(boundDisciplines.size, disciplines.length),
    };

    let level = null;
    if (codes.length > 0 && coverage.task >= COVERAGE) level = 'task';
    else if (codes.length > 0 && coverage.costCode >= COVERAGE) level = 'costCode';
    else if (disciplines.length > 0 && coverage.discipline >= COVERAGE) level = 'discipline';
    else if (activities.length > 0 || boundCount > 0) level = 'tradeClass';

    return {
        level,
        label: level ? LEVEL_LABEL[level] : 'Nothing scheduled',
        coverage,
        codeCount: codes.length,
        boundCount,
        taskCount,
        disciplineCount: disciplines.length,
        unboundCodes,
        unboundDisciplines,
        thinCodes,
    };
};

/**
 * The flag the Baseline view raises: does the schedule reach the level the job
 * owes, and if not, which rows are the reason.
 *
 * Severity is 'error' when the schedule is more than one level short or missing
 * entirely, 'warn' when it is one level short — one level is a conversation,
 * two is a schedule that was never built for this job.
 */
export const validateSchedule = ({ required, achieved } = {}) => {
    if (!required) return { ok: true, severity: 'none', message: '', offenders: [] };

    if (!achieved || !achieved.level) {
        return {
            ok: false,
            severity: 'error',
            message: `This job needs a schedule detailed to ${LEVEL_LABEL[required.level].toLowerCase()}. `
                + 'Nothing is scheduled yet — upload the schedule and map it to the budget below.',
            offenders: [],
        };
    }

    const gap = rankOf(required.level) - rankOf(achieved.level);
    if (gap <= 0) {
        return {
            ok: true,
            severity: 'ok',
            message: `Schedule is detailed to ${achieved.label.toLowerCase()}`
                + `${gap < 0 ? ' — finer than this job requires, which is fine' : ''}.`,
            offenders: [],
        };
    }

    // Name what to fix: every unit short of the REQUIRED level, not just the
    // ones short of the level the schedule happened to reach. At task level an
    // unbound code is as much a gap as a single-bar one, and a schedule with
    // nothing mapped is all gap.
    let offenders;
    let detail;
    if (required.level === 'task') {
        offenders = [...achieved.unboundCodes, ...achieved.thinCodes].sort();
        const thin = achieved.thinCodes.length;
        const none = achieved.unboundCodes.length;
        detail = [
            none ? `${none} cost code${none === 1 ? ' has' : 's have'} no schedule activity` : null,
            thin ? `${thin} ${none ? 'more have' : `cost code${thin === 1 ? ' has' : 's have'}`} only one schedule activity` : null,
        ].filter(Boolean).join(', ');
    } else if (required.level === 'discipline') {
        offenders = achieved.unboundDisciplines;
        detail = `${offenders.length} of ${achieved.disciplineCount} disciplines have no schedule activity`;
    } else {
        offenders = achieved.unboundCodes;
        detail = `${offenders.length} of ${achieved.codeCount} cost codes have no schedule activity`;
    }

    return {
        ok: false,
        severity: gap >= 2 ? 'error' : 'warn',
        message: `This job needs ${LEVEL_LABEL[required.level].toLowerCase()} detail; the schedule only reaches `
            + `${achieved.label.toLowerCase()}. ${detail}.`,
        offenders,
    };
};

/**
 * The trade classes the schedule covers, for the ladder's bottom band.
 * `workTypes` comes from jobWorkTypes() — the job's own work type when JDE
 * carries one, otherwise the disciplines parsed off the phase descriptions.
 */
export const tradeClassCoverage = (workTypes = [], activities = []) => {
    if (workTypes.length === 0) return { covered: [], uncovered: [] };
    // With one work type the whole schedule is that trade by definition; only a
    // multi-trade job can leave one unscheduled.
    if (workTypes.length === 1) {
        return activities.length > 0
            ? { covered: [...workTypes], uncovered: [] }
            : { covered: [], uncovered: [...workTypes] };
    }
    const covered = [];
    const uncovered = [];
    workTypes.forEach((wt) => {
        const hit = activities.some((a) => nameScore(wt, activityName(a)) > 0
            || String(a?.WBS_Path ?? a?.wbsPath ?? '').toLowerCase().includes(String(wt).toLowerCase()));
        (hit ? covered : uncovered).push(wt);
    });
    return { covered, uncovered };
};

export const keyOfActivity = keyOf;
